import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useRouter } from '@vl-core/useRouter';
import { useQuery } from '@tanstack/react-query';
import { Button, Row, Col, Spin, Input, Alert, Card, Modal, Form, Result, Space, Select, Empty, Radio } from 'antd';
import { DateTime } from 'luxon';
import useApi from 'vl-common/src/hooks/useApi';
import useUser from 'vl-core/hooks/useUser';
import styled from '@emotion/styled';
import useSystemType from 'vl-core/hooks/useSystemType';
import DisplayAvailableSlotsFace2Face from 'vl-common/src/components/Face2FaceBooking/DisplayAvailableSlotsFace2Face';
import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import useDisplayCancellationAndExcessText from '@vl-core/hooks/useDisplayCancellationAndExcessText';
import { getF2FConsultants } from 'vl-common/src/schemas';
import { useTranslation } from 'react-i18next';
import { useBookingFlowData } from '../../hooks/useBookingFlowData';
import { Case } from '../../schemas/shared';

const { Search } = Input;

type Slot = getF2FConsultants.Response['consultantSlots'][number]['slots'][number];
type Consultant = getF2FConsultants.Response['consultantSlots'][number];

const GenderFilters = {
  All: null,
  Male: 'M',
  Female: 'F'
} as const;

const ChooseFace2FaceClinician = ({ title, history }: { title: string; history?: any }) => {
  const { t } = useTranslation('common');
  const { caseData, patientData, bookingData } = useBookingFlowData();

  const [selectedSlot, setSelectedSlot] = useState(null);
  const [selectedSlotInfo, setSelectedSlotInfo] = useState(null);

  const [selectedAuthCancelReason, setSelectedAuthCancelReason] = useState(null);

  const [taskMessage, setTaskMessage] = useState('');
  const [taskError, setTaskError] = useState(false);

  const [taskMessageModalVisible, setTaskMessageModalVisible] = useState(false);
  const [taskMessageConfirmModalVisible, setTaskMessageConfirmModalVisible] = useState(false);
  const [finalModalVisible, setFinalModalVisible] = useState(false);
  const [failureModalVisible, setFailureModalVisible] = useState(false);

  const [buttonLoading, setButtonLoading] = useState(false);
  const [taskLoading, setTaskLoading] = useState(false);

  const user = useUser();
  const { Api } = useApi();
  const router = useRouter();
  const systemType = useSystemType();
  const cancellationAndExcessText = useDisplayCancellationAndExcessText();

  const [patientPostcode, setPatientPostcode] = useState(patientData?.postcode || undefined);
  const [postcodeInput, setPostcodeInput] = useState(patientData?.postcode || undefined);
  const [clinicianGenderFilter, setClinicianGenderFilter] = useState(GenderFilters.All);

  const [currentPage, setCurrentPage] = useState(1);

  const isConfirmModalVisible = useMemo(() => !!selectedSlot && !!selectedSlotInfo, [selectedSlot, selectedSlotInfo]);

  const closeConfirmModal = useCallback(() => {
    setSelectedSlot(null);
    setSelectedSlotInfo(null);
  }, []);

  useEffect(() => {
    if (patientData?.postcode) {
      setPostcodeInput(patientData.postcode);
      setPatientPostcode(patientData.postcode);
    }
  }, [patientData?.postcode]);

  const typeCode = bookingData?.bookedByAdmin
    ? bookingData.adminBookingType
    : // @ts-expect-error patient_clin_subtype_code does not appear in new cases?
      caseData?.patient_clin_subtype_code;

  // Only enable call when all request body data is present
  const getF2fClinsEnabled = Boolean(
    bookingData?.patientId &&
    caseData?.spec_cat_code &&
    typeCode &&
    bookingData?.caseId &&
    bookingData?.previousApptId &&
    bookingData?.apptType &&
    patientPostcode
  );

  const { data: face2faceClinicians = [], isLoading } = useQuery({
    queryKey: ['getF2FClinicians', bookingData, patientPostcode],
    queryFn: async () => {
      const face2faceCliniciansList = await Api.booking.getF2FClinicians({
        patientGuid: bookingData?.patientId,
        clinicianSpecCat: caseData?.spec_cat_code,
        caseId: bookingData?.caseId,
        apptId: bookingData?.previousApptId,
        fromDate:
          // @ts-expect-error will work even if appAuthFromDate is null/undefined
          bookingData?.apptAuthFromDate > new Date()
            ? bookingData?.apptAuthFromDate?.toISOString?.().substring(0, 10)
            : DateTime.now().toFormat('yyyy-MM-dd'),
        typeCode,
        postcode: patientPostcode
      });
      return face2faceCliniciansList?.consultantSlots;
    },
    refetchOnWindowFocus: false, // only poll when we need to, not on navigating away/back to the tab
    enabled: getF2fClinsEnabled
  });

  const filteredFace2faceClinicians = useMemo(() => {
    if (!clinicianGenderFilter) return face2faceClinicians;

    return face2faceClinicians.filter((clinician: Consultant) => clinician.sex === clinicianGenderFilter);
  }, [clinicianGenderFilter, face2faceClinicians]);

  const itemsPerPage = 3;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const paginatedFace2FaceClinicians = filteredFace2faceClinicians.slice(indexOfFirstItem, indexOfLastItem);
  const isLastPage = indexOfLastItem >= filteredFace2faceClinicians.length;

  const isLoadingFace2faceClinicians = isLoading && !!patientPostcode;

  const { data: userAuthCancelReasons = [] } = useQuery({
    queryKey: ['patientsCaseMetaData', bookingData?.patientId],
    queryFn: async () => {
      const caseMetadataList = await Api.meta.patientsCaseMetaData(bookingData?.patientId);

      const userAuthCancelReasons = caseMetadataList?.data.meta[0].user_auth_cancel_reasons;

      return userAuthCancelReasons.map((reason) => ({
        label: reason.auth_cancel_reason_desc,
        value: reason.auth_cancel_reason_code
      }));
    },
    enabled: !!bookingData?.patientId
  });

  const confirmAppointment = async () => {
    try {
      setButtonLoading(true);
      const res = await Api.booking.bookF2FClinician({
        ...selectedSlot,
        // @ts-expect-error
        appt_id: bookingData?.previousApptId || selectedSlot?.appt_id
      });
      if (res.status === 200 && bookingData?.previousApptId) {
        await Api.file.pdfGen(
          {
            preview: false,
            letter_code: 'F2F_SPEC'
          },
          {
            appointmentID: bookingData?.previousApptId,
            userGUID: (caseData as Case).clinician_user_guid,
            patientUserGUID: bookingData.patientId
          }
        );
      }
      closeConfirmModal();
      setFinalModalVisible(true);
      setButtonLoading(false);
    } catch (error) {
      if (error.response?.status === 400) {
        closeConfirmModal();
        setFailureModalVisible(true);
        setButtonLoading(false);
      }
    }
  };

  const removeF2FAuthorisation = async () => {
    let res;
    if (bookingData?.patientId && bookingData?.caseId && selectedAuthCancelReason) {
      res = await Api.booking.removeAuthorisation(
        bookingData?.patientId,
        bookingData.caseId,
        selectedAuthCancelReason,
        taskMessage
      );
    }

    setTaskLoading(false);
    return res;
  };

  const formatSlotInPatientTimezone = (slot: Slot) => {
    return DateTime.fromISO(`${slot.slotDate}T${slot.slotTime}Z`)
      .setZone(patientData?.timezone)
      .toFormat('dd/MM/yyyy @ HH:mm');
  };

  const StyledHeader = styled.h1`
    text-align: center;
    margin-bottom: 20px;
    ${systemType === 'vl-admin' ? 'font-size: 30px' : ''}
  `;
  const StyledExcessContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;

    p {
      max-width: 70%;
      text-align: center;

      @media (max-width: 768px) {
        max-width: 100%;
      }
    }
  `;

  return (
    <>
      {title && <StyledHeader>{title}</StyledHeader>}
      <StyledExcessContainer>
        <p>{cancellationAndExcessText}</p>
      </StyledExcessContainer>
      <Form
        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginBottom: '20px' }}
        onFinish={() => {
          setPatientPostcode(postcodeInput);
        }}
      >
        <Form.Item
          htmlFor="patient_postcode"
          label={t('The list will show the nearest clinicians to the given postcode')}
          style={{ marginBottom: '7px' }}
        >
          <Search
            value={postcodeInput}
            name="patient_postcode"
            placeholder={t('Search postcode')}
            onChange={(e) => {
              setPostcodeInput(e.target.value);
            }}
            onSearch={() => {
              setPatientPostcode(postcodeInput);
            }}
            style={{ width: 200 }}
          />
        </Form.Item>
        {!patientPostcode && !postcodeInput && (
          <Alert
            style={{ display: 'flex' }}
            showIcon
            message={t('Please enter postcode to see available face-to-face appointment slots')}
            type="warning"
          />
        )}
      </Form>

      <Spin size="large" spinning={isLoadingFace2faceClinicians} tip="Loading clinicians" style={{ width: '100%' }} />

      {!isLoadingFace2faceClinicians && filteredFace2faceClinicians.length !== 0 && (
        <div style={{ textAlign: 'center' }}>
          {t('All times displayed in ')}
          {systemType === 'vl-admin' ? t("the patient's") : t('your local')} {t('timezone,')} {patientData?.timezone}
        </div>
      )}

      <Form.Item label={t('Gender')} style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 0 }}>
        <Radio.Group
          onChange={(e) => {
            setClinicianGenderFilter(e.target.value);
          }}
          value={clinicianGenderFilter}
        >
          {Object.entries(GenderFilters).map(([key, value]) => (
            <Radio key={key} value={value}>
              {key}
            </Radio>
          ))}
        </Radio.Group>
      </Form.Item>

      <Space direction="vertical" size={30} style={{ width: '100%' }}>
        {!isLoadingFace2faceClinicians && filteredFace2faceClinicians.length === 0 ? (
          <Empty
            description={t('No available face-to-face appointment slots for {{patientPostcode}}', { patientPostcode })}
          />
        ) : (
          paginatedFace2FaceClinicians?.map((clinician) => (
            <Card
              key={clinician?.slots?.[0]?.PractitionerId}
              title={`${clinician?.title} ${clinician?.first_name} ${clinician?.last_name}`}
              styles={{ body: { padding: 0 } }}
            >
              <Row>
                <Col xs={24} sm={24} md={24} lg={8} xl={6}>
                  <LabelValuePairs
                    values={[
                      {
                        label: clinician?.subtype === 'PHYSIO' ? 'HCPC Number' : 'GMC Number',
                        value: clinician?.gmcCode
                      },
                      { label: 'Clinician Type', value: clinician?.subtype_desc },
                      { label: 'Specialty', value: clinician?.cat_name },
                      { label: 'Gender', value: clinician?.sex_desc },
                      { label: 'Service', value: clinician?.service },
                      { label: 'Location', value: clinician?.locationData?.name },
                      { label: 'Distance', value: clinician?.locationData?.displayDistance },
                      {
                        label: 'First available slot',
                        value: formatSlotInPatientTimezone(clinician?.slots[0])
                      }
                    ]}
                  />
                </Col>

                <Col xs={24} sm={24} md={24} lg={16} xl={18}>
                  <DisplayAvailableSlotsFace2Face
                    clinician={clinician}
                    setSelectedSlot={setSelectedSlot}
                    setSelectedSlotInfo={setSelectedSlotInfo}
                    // @ts-expect-error
                    case_id={bookingData?.caseId}
                    // @ts-expect-error
                    appt_id={bookingData?.previousApptId}
                    patientData={patientData}
                  />
                </Col>
              </Row>
            </Card>
          ))
        )}

        {systemType !== 'vl-admin' && !isLoadingFace2faceClinicians && (
          <Card
            title="No suitable face-to-face appointments?"
            style={{
              maxWidth: 800,
              margin: '0 auto'
            }}
            extra={
              <Button size="large" onClick={() => setTaskMessageModalVisible(true)} data-testid="request-alternative">
                {t('Ask for a different appointment')}
              </Button>
            }
          >
            {filteredFace2faceClinicians?.length < 1
              ? t('Unfortunately we were unable to find any available face-to-face appointment slot')
              : t('If you are unable to attend one of the above face-to-face appointments, please')}
            {t('request an alternative face-to-face appointment.')}
          </Card>
        )}
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            width: '100%',
            justifyContent: 'space-between',
            paddingBottom: '20px'
          }}
          className="pagination"
        >
          <Button
            data-testId={'previous-button'}
            icon={<LeftOutlined />}
            disabled={currentPage <= 1}
            onClick={() => setCurrentPage((val) => val - 1)}
          >
            {t('Previous')}
          </Button>

          <Button
            data-testId={'next-button'}
            disabled={isLastPage || (filteredFace2faceClinicians?.length && filteredFace2faceClinicians?.length < 1)}
            onClick={() => {
              setCurrentPage((val) => val + 1);
            }}
            icon={<RightOutlined />}
          >
            {t('Next')}
          </Button>
        </div>
      </Space>

      {/* MODALS */}
      {/* Write and submit alt F2F appointment request */}
      <Modal
        title="Request an alternative face-to-face appointment"
        centered
        open={taskMessageModalVisible}
        onCancel={() => setTaskMessageModalVisible(false)}
        okText="Request"
        okButtonProps={{ loading: taskLoading, disabled: !taskMessage }}
        onOk={async () => {
          setTaskLoading(true);
          const removeAuthRes = await removeF2FAuthorisation();
          if (removeAuthRes.status === 202) {
            setTaskMessageModalVisible(false);
            setTaskLoading(false);
            setTaskError(false);
            setTaskMessageConfirmModalVisible(true);
          } else {
            setTaskLoading(false);
            setTaskError(true);
          }
        }}
      >
        {t(
          "Please tell us why you need a different clinician. Please also tell us when you're available for an appointment during the next three weeks."
        )}
        {taskError && (
          <Alert
            className="login-alert"
            message={t('There was an error creating the task, please try again')}
            type="error"
            closable
            afterClose={() => setTaskError(false)}
            showIcon
          />
        )}
        <Form layout="vertical">
          <Form.Item label={t('Message')} htmlFor="taskMessage">
            <Input.TextArea
              name="taskMessage"
              rows={6}
              value={taskMessage}
              onChange={(e) => setTaskMessage(e.target.value)}
            />
          </Form.Item>

          {userAuthCancelReasons.length > 0 && (
            <Form.Item label={t('Reason for request')} htmlFor="taskReason">
              <Select
                options={userAuthCancelReasons}
                value={selectedAuthCancelReason}
                onChange={setSelectedAuthCancelReason}
                data-testid="auth-cancel-reason-select"
              />
            </Form.Item>
          )}
        </Form>
      </Modal>
      {/* Confirm F2F booking */}
      <Modal
        title="You are about to book a face-to-face appointment."
        centered
        open={isConfirmModalVisible}
        okText="Confirm"
        onOk={() => confirmAppointment()}
        okButtonProps={{ loading: buttonLoading }}
        cancelText="Cancel"
        className={systemType !== 'vl-admin' ? 'modal' : ''}
        onCancel={closeConfirmModal}
      >
        <p>{cancellationAndExcessText}</p>
        {t('Please verify the information below:')}
        <SummaryCard>
          {systemType === 'vl-admin' && (
            <>
              <CardSection
                title="Patient"
                description={`${patientData?.title || ''} ${patientData?.first_name || ''} ${patientData?.last_name || ''
                  }`}
              />
              <CardSection title="Date of Birth" description={patientData?.dob?.toLocaleDateString()} />
            </>
          )}
          <CardSection title="Clinician" description={selectedSlotInfo?.clin_name} />
          <CardSection title="Appointment Date" description={selectedSlotInfo?.time} />
          <CardSection title="Location" description={selectedSlotInfo?.location} fullWidth />
        </SummaryCard>
      </Modal>
      {/* Success state when an F2F appointment is booked */}
      <Modal
        centered
        open={finalModalVisible}
        footer={null}
        closable={false}
        onCancel={() => {
          setFinalModalVisible(false);
          setTaskMessageConfirmModalVisible(false);
          const route = { CLIENTADMIN: 'admin', ADMIN: 'referrer-admin', REFERRER: 'referrer' }[user?.user_type_code];
          if (systemType === 'vl-admin') {
            router.push(`/${route}/appointments`);
          } else {
            router.push('/patients');
          }
        }}
      >
        <Result
          status="success"
          title={t('All booked')}
          extra={[
            <Button
              key="submit"
              onClick={() => {
                setFinalModalVisible(false);
                setTaskMessageConfirmModalVisible(false);
                if (systemType === 'vl-admin') {
                  history.push(bookingData?.redirectRoute);
                } else {
                  router.push('/patients');
                }
              }}
              data-testid="return-to-dashboard"
            >
              {t('Return to dashboard')}
            </Button>
          ]}
        />
      </Modal>
      {/* Failure state when an F2F appointment is not booked */}
      <Modal
        centered
        open={failureModalVisible}
        footer={null}
        closable={false}
        onCancel={() => {
          setFailureModalVisible(false);
        }}
      >
        <Result
          status="error"
          title="There was an error booking your appointment. Please try again or request an alternative"
          extra={[
            <Button
              key="submit"
              onClick={() => {
                setFailureModalVisible(false);
              }}
              data-testid="try-again"
            >
              {t('Please try again')}
            </Button>
          ]}
        />
      </Modal>
      {/* Confirm that an alt F2F appointment request has been submitted */}
      <Modal centered open={taskMessageConfirmModalVisible} footer={null} closable={false}>
        <Result
          status="success"
          title="Alternative appointment requested"
          subTitle="Our contact centre has been notified and will be in touch soon to arrange an alternative appointment."
          extra={[
            <Button
              key="submit"
              onClick={() => {
                setTaskMessageConfirmModalVisible(false);
                const route = { CLIENTADMIN: 'admin', ADMIN: 'referrer-admin', REFERRER: 'referrer' }[
                  user?.user_type_code
                ];
                if (systemType === 'vl-admin') {
                  router.push(`/${route}/appointments`);
                } else {
                  router.push('/patients');
                }
              }}
              data-testid="return-to-dashboard"
            >
              {t('Return to dashboard')}
            </Button>
          ]}
        />
      </Modal>
    </>
  );
};

const SummaryCard = styled(Card)`
  margin: 1em 0 0 0;

  .ant-card-body {
    display: flex;
    flex-wrap: wrap;
  }
`;

const CardSection = ({ title = '', description = '', fullWidth = false }) => {
  return (
    <Card.Grid hoverable={false} style={{ width: fullWidth ? '100%' : '50%' }}>
      <Card.Meta title={title} description={description} />
    </Card.Grid>
  );
};

const StyledDl = styled.dl`
  display: grid;
  grid-gap: 0.25em;
  grid-template-columns: 1fr;
  padding: 24px;
  background: var(--color-Alabaster);
  border-right: none;
  height: 100%;

  div {
    display: grid;
    grid-template-columns: 1fr 1fr;
  }
  dt {
    line-height: 15px;
    font-weight: 500;
    font-size: 15px;
  }
  dd {
    line-height: 1;
    margin: 0 0 0.5em 0;
    opacity: 0.7;
  }

  @media (min-width: 576px) {
    grid-template-columns: 1fr 1fr;

    div {
      grid-template-columns: 1fr;
    }
  }

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr 1fr;
  }

  @media (min-width: 880px) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
  }

  @media (min-width: 992px) {
    grid-template-columns: 1fr;
    border-right: 1px solid #e8e8e8;
    border-bottom: none;
  }
`;
/**
 * Displays key-value pairs responsively
 *
 * @param values - An array of objects with `label` and `value`
 * properties. Optionally a `numeric` boolean property (if true, the value will
 * use a numeric font variant, and a `code` boolean property (if true, the value
 * will be displayed in `<code>` tags).
 */
export function LabelValuePairs({ values }) {
  function Value({ str, code, numeric }) {
    if (code) return <code>{str}</code>;
    if (numeric) return <span className="numeric">{str}</span>;
    return str || [];
  }
  return (
    <StyledDl>
      {values.map((value) => {
        const { label, value: str, numeric, code } = value;
        return (
          <div key={label}>
            <dt>{label}</dt>
            <dd>
              <Value {...{ str, numeric, code }} />
            </dd>
          </div>
        );
      })}
    </StyledDl>
  );
}

export default ChooseFace2FaceClinician;
